import 'echarts/lib/chart/bar';
import 'echarts/lib/component/grid';
import 'echarts/lib/component/title';
import 'echarts/lib/component/tooltip';
import * as echarts from 'echarts/lib/echarts';
import React, { useLayoutEffect, useRef } from 'react';

interface Metric {
  createdAt: string;
  downloadOperations: number;
}

interface Bucket {
  id: string;
  name?: string;
  historicalBucketMetrics?: Metric[];
}

interface DownloadsChartProps {
  buckets: Bucket[];
  start: string;
  end: string;
}

const DownloadsChart: React.FC<DownloadsChartProps> = ({ buckets, start, end }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', {
      month: 'short',
      timeZone: 'UTC',
    });
    return `${day} ${month}`;
  };

  useLayoutEffect(() => {
    if (!chartRef.current) return;

    const myChart = echarts.init(chartRef.current);

    // Generate all formatted dates between start and end
    const generateDateRange = (start: Date, end: Date) => {
      const dates: string[] = [];
      const current = new Date(start);
      while (current <= end) {
        dates.push(formatDate(current.toISOString()));
        current.setUTCDate(current.getUTCDate() + 1);
      }
      return dates;
    };
    let sortedDates = generateDateRange(new Date(start), new Date(end));
    let seriesData: any[] = [];

    if (buckets && buckets.length > 0) {
      const allDates = new Set<string>();
      let hasMetrics = false;

      buckets.forEach((bucket) => {
        const metrics = bucket.historicalBucketMetrics || [];

        if (metrics.length > 0) {
          hasMetrics = true;
        }

        metrics.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

        metrics.forEach((metric) => {
          allDates.add(formatDate(metric.createdAt));
        });
      });

      // Use metrics-based dates only if there are any
      if (hasMetrics) {
        sortedDates = Array.from(allDates).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

        buckets.forEach((bucket) => {
          const metricsMap = new Map(
            (bucket.historicalBucketMetrics || []).map((m) => [formatDate(m.createdAt), m.downloadOperations])
          );

          const data = sortedDates.map((date) => metricsMap.get(date) || 0);

          seriesData.push({
            name: bucket.name || bucket.id,
            type: 'bar',
            data,
          });
        });
      } else {
        // No metrics at all, use default dates with 0s
        seriesData = [
          {
            name: 'No Data',
            type: 'bar',
            data: sortedDates.map(() => 0),
          },
        ];
      }
    } else {
      // No buckets at all
      seriesData = [
        {
          name: 'No Data',
          type: 'bar',
          data: sortedDates.map(() => 0),
        },
      ];
    }

    const hasRealData = seriesData.some((series) => series.data.some((value: number) => value > 0));

    // ECharts options (same as your version)
    const option = {
      tooltip: { trigger: 'axis' },
      legend: { data: seriesData.map((s) => s.name) },
      xAxis: {
        type: 'category' as const,
        data: sortedDates,
        axisLine: { lineStyle: { color: '#848F99' } },
        axisLabel: { rotate: 45, color: '#848F99' },
        axisTick: { lineStyle: { color: '#848F99' } },
      },
      dataZoom: [
        {
          handleIcon:
            'M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
          handleSize: '80%',
          handleStyle: {
            color: '#fff',
            shadowBlur: 3,
            shadowColor: 'rgba(0, 0, 0, 0.25)',
            shadowOffsetX: 2,
            shadowOffsetY: 2,
          },
        },
      ],
      grid: {
        left: '1%',
        right: '3%',
        bottom: '6%',
        containLabel: true,
      },
      yAxis: {
        type: 'value' as const,
        name: 'Size (GB)',
        min: 0,
        ...(hasRealData ? {} : { max: 100 }),
        splitLine: {
          lineStyle: {
            color: '#EEF0F2',
          },
        },
        axisLine: { lineStyle: { color: '#848F99' } },
        axisLabel: { color: '#848F99' },
        axisTick: { lineStyle: { color: '#848F99' } },
      },
      series: seriesData,
    };

    myChart.setOption(option as any);
    const handleResize = () => myChart.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      myChart.dispose();
    };
  }, [buckets, start, end]);

  return <div ref={chartRef} style={{ width: '100%', height: '300px' }} />;
};

export default DownloadsChart;
